import { Challenge, ChallengeTypeEnum } from '~/api';
import { FormItemProps } from '~/components/FormBuilder/props';
import { useTranslate } from '@refinedev/core';
import { lowerCase } from 'lodash';
import { sentenceCase } from '@scalingworks/refine-react-admin';
import { useGetChallengePlatformsQuery } from '~/generated/hooks';

export const challengeFormBuilder = (challenge?: Challenge, formHook?: any): FormItemProps[] => {
  const t = useTranslate();

  const { data: challengePlatforms } = useGetChallengePlatformsQuery();
  
  // Create platform options with null check
  const platformOptions = challengePlatforms?.getChallengePlatforms?.items?.map((platform) => ({
    label: sentenceCase(platform.platformType),
    value: platform.id,
  })) || [];

  return [
    {
      title: t('challenges.form.uploadImage', { fallback: 'Upload Image' }),
      placeholder: t('challenges.placeholder.uploadImage', { fallback: 'Please drop image here or browse to upload' }),
      name: 'asset',
      type: 'upload',
      defaultValue: challenge?.asset?.source ? [challenge.asset.source] : [],
      fileType: 'image/*',
    },
    {
      title: t('challenges.form.title', { fallback: 'Title' }),
      placeholder: t('challenges.placeholder.title', { fallback: 'Please enter challenge title' }),
      name: 'title',
      type: 'text',
      required: true,
      defaultValue: challenge?.title || undefined,
    },
    {
      title: t('challenges.form.description', { fallback: 'Description' }),
      placeholder: t('challenges.placeholder.description', { fallback: 'Please enter challenge description' }),
      name: 'description',
      type: 'text',
      multiline: true,
      required: true,
      defaultValue: challenge?.description || undefined,
    },
    {
      title: t('challenges.form.startDate', { fallback: 'Start Date' }),
      placeholder: t('challenges.placeholder.startDate', { fallback: 'Please select a start date' }),
      name: 'startDate',
      type: 'date-picker',
      required: true,
      defaultValue: challenge?.startDate,
      props: {
        showTimeSelect: true,
        min: new Date(), // Prevent selecting past dates
      },
    },
    {
      title: t('challenges.form.endDate', { fallback: 'End Date' }),
      placeholder: t('challenges.placeholder.endDate', { fallback: 'Please select an end date' }),
      name: 'endDate',
      type: 'date-picker',
      defaultValue: challenge?.endDate,
      props: {
        showTimeSelect: true,
        min: formHook?.watch ? 
          (formHook.watch('startDate') ? new Date(formHook.watch('startDate')) : new Date()) : 
          new Date(), // Ensure end date is not earlier than start date
      },
    },
    {
      title: t('challenges.form.enabled', { fallback: 'Enabled' }),
      placeholder: t('challenges.placeholder.enabled', { fallback: 'Enable or disable this challenge' }),
      name: 'enabled',
      type: 'switch',
      defaultValue: challenge?.enabled ?? true,
    },
    {
      title: t('challenges.form.type', { fallback: 'Type' }),
      placeholder: t('challenges.placeholder.type', { fallback: 'Please select challenge type' }),
      name: 'type',
      type: 'select',
      required: true,
      defaultValue: challenge?.type,
      options: Object.values(ChallengeTypeEnum).map((type) => ({
        label: sentenceCase(type),
        value: type,
      })),
    },
    {
      title: t('challenges.form.limitPerCustomer', { fallback: 'Limit Per Customer' }),
      placeholder: t('challenges.placeholder.limitPerCustomer', { fallback: 'Please enter maximum submission limit per customer' }),
      name: 'limitPerCustomer',
      type: 'number',
      defaultValue: challenge?.limitPerCustomer || undefined,
      props: {
        step: 1,
        min: 1, // Ensure positive numbers only
        allowEmpty: true, // Allow empty value to be treated as null
      },
    },
  ];
};
